/**
 * A one-click way to get the Deezer cookie out of a logged-in browser.
 *
 * The `arl` cookie is HttpOnly — confirmed by listing cookie names on a
 * logged-in deezer.com, where neither `arl` nor `sid` appears — so no amount of
 * `document.cookie` will reach it. That is exactly why the manual route runs
 * through devtools, which can see cookies scripts cannot.
 *
 * The way around it is not to read the cookie at all. Deezer's own gateway has
 * `user.getArl`, which returns the token in the JSON *body* for whatever session
 * the request carries. Calling it needs no password and no api_token — an
 * unauthenticated call answers `NEED_USER_AUTH_REQUIRED`, which is to say the
 * only thing it wants is a logged-in user.
 *
 * It has to run *on* deezer.com. Tunes cannot make this call itself: gw-light.php
 * sends no `Access-Control-Allow-Origin`, so the browser blocks a cross-origin
 * read of the response. A bookmarklet is code running inside Deezer's own origin,
 * as the person clicking it — which is why the same-origin rules simply do not
 * bite.
 *
 * The value comes back in the URL *fragment*. Fragments are never sent to the
 * server, so the cookie stays out of nginx and Django logs on the way in; the
 * settings page strips it from history before doing anything else.
 */
export function deezerBookmarklet(origin: string): string {
  const target = `${origin}/settings?section=providers`
  // Deliberately one line and dependency-free: this gets pasted into a bookmark.
  return [
    'javascript:(async()=>{try{',
    "const r=await fetch('https://www.deezer.com/ajax/gw-light.php?method=user.getArl&input=3&api_version=1.0&api_token=null',{credentials:'include'});",
    'const j=await r.json();',
    // Clients disagree on the shape: most read `results` as a bare string, one
    // documents `results.arl`. Accept either rather than betting on one.
    "const a=typeof j.results==='string'?j.results:(j.results&&j.results.arl);",
    "if(!/^[a-f0-9]{192}$/i.test(a||''))throw new Error(j.error&&Object.keys(j.error).length?'not signed in to Deezer':'unexpected reply');",
    `location.href=${JSON.stringify(target)}+'#arl='+a;`,
    "}catch(e){alert('Could not read your Deezer cookie: '+e.message+String.fromCharCode(10,10)+'Open deezer.com, make sure you are signed in, and click again.');}})()",
  ].join('')
}
